import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Download, Trash2, ShieldCheck, Loader2, Crown, Sparkles, AudioLines } from "lucide-react";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtDate, usageText, GRADIENTS } from "@/lib/constants";
import { LogoMark } from "@/components/Logo";
import { useTheme } from "@/context/ThemeContext";

const GradientPicker = ({ value, onChange }) => (
  <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3" data-testid="gradient-picker">
    {GRADIENTS.map((g) => {
      const active = (value || "electrique") === g.id;
      return (
        <button key={g.id} data-testid={`gradient-option-${g.id}`} onClick={() => onChange(g.id)}
          className={`card-flat card-hover group p-3 text-left ${active ? "ring-2 ring-foreground ring-offset-2 ring-offset-background" : ""}`}>
          <span className="block h-16 rounded-xl" style={{ background: `linear-gradient(110deg, hsl(${g.from}), hsl(${g.to}))` }} />
          <span className="mt-2.5 flex items-center justify-between px-1 text-sm font-semibold">{g.name}{active && <span className="text-xs font-medium text-muted-foreground">Actif</span>}</span>
        </button>
      );
    })}
  </div>
);

const AI = [["claude", "Claude Opus 5.5", "Par défaut — notes très structurées"], ["gpt", "ChatGPT (GPT 6 Sol)", "Modèle d'OpenAI"]];
const STT = [["whisper", "Whisper (OpenAI)", "Par défaut — signale les passages incertains"], ["wizper", "Wizper — Whisper v3 Large", "Très précis et rapide (fal.ai)"], ["scribe", "ElevenLabs Scribe", "Excellente qualité (fal.ai)"]];
const RETENTION = [[30, "30 jours"], [90, "90 jours"], [180, "6 mois"], [365, "1 an"]];

const Row = ({ title, text, children }) => (
  <div className="flex flex-col gap-3 border-b py-5 last:border-b-0 sm:flex-row sm:items-center sm:justify-between">
    <div><p className="font-semibold">{title}</p><p className="text-sm text-muted-foreground">{text}</p></div>
    <div className="shrink-0">{children}</div>
  </div>
);

const Danger = ({ testid, label, title, text, onConfirm }) => (
  <AlertDialog>
    <AlertDialogTrigger asChild><button data-testid={testid} className="inline-flex items-center gap-2 rounded-full border border-destructive/40 px-4 py-2 text-sm font-semibold text-destructive hover:bg-destructive/10"><Trash2 className="h-4 w-4" /> {label}</button></AlertDialogTrigger>
    <AlertDialogContent className="rounded-3xl">
      <AlertDialogHeader><AlertDialogTitle>{title}</AlertDialogTitle><AlertDialogDescription>{text}</AlertDialogDescription></AlertDialogHeader>
      <AlertDialogFooter><AlertDialogCancel className="rounded-full">Annuler</AlertDialogCancel><AlertDialogAction data-testid={`${testid}-confirm`} onClick={onConfirm} className="rounded-full bg-destructive text-destructive-foreground hover:bg-destructive/90">Supprimer définitivement</AlertDialogAction></AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

const Choice = ({ testid, value, options, onChange }) => (
  <Select value={String(value)} onValueChange={onChange}>
    <SelectTrigger data-testid={testid} className="h-10 w-64 rounded-xl bg-card"><SelectValue /></SelectTrigger>
    <SelectContent>{options.map(([v, l, d]) => <SelectItem key={v} value={String(v)} data-testid={`${testid}-option-${v}`}>{l}{d ? <span className="ml-1 text-xs text-muted-foreground">· {d}</span> : null}</SelectItem>)}</SelectContent>
  </Select>
);

export default function Profile() {
  const { user, setUser } = useAuth();
  const { setGradient } = useTheme();
  const navigate = useNavigate();
  const [exporting, setExporting] = useState(false);

  const patch = async (body, msg) => {
    try { const { data } = await api.patch("/me", body); setUser(data); toast.success(msg); } catch (e) { toast.error(errMsg(e)); }
  };
  const exportData = async () => {
    setExporting(true);
    try {
      const { data } = await api.get("/me/export");
      const a = document.createElement("a");
      a.href = URL.createObjectURL(new Blob([JSON.stringify(data, null, 2)], { type: "application/json" }));
      a.download = "mes-donnees-notemyclass.json";
      a.click();
    } catch (e) { toast.error(errMsg(e)); } finally { setExporting(false); }
  };
  const deleteCourses = async () => {
    try { const { data } = await api.delete("/me/courses"); toast.success(`${data.deleted} cours supprimé(s) définitivement.`); } catch (e) { toast.error(errMsg(e)); }
  };
  const deleteAccount = async () => {
    try { await api.delete("/me"); setUser(null); toast.success("Ton compte et toutes tes données ont été supprimés."); navigate("/"); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="profile-page" className="max-w-3xl">
      <p className="eyebrow">Profil</p>
      <div className="mt-3 flex items-center gap-4">
        <LogoMark className="h-14 w-14 rounded-2xl" iconClass="h-7 w-7" testid="profile-avatar" />
        <div><h1 className="text-3xl font-extrabold tracking-tight" data-testid="profile-name">{user?.name}</h1><p className="text-sm text-muted-foreground">{user?.email} · membre depuis le {fmtDate(user?.created_at)}</p></div>
      </div>

      <section className="card-flat mt-8 px-6">
        <Row title={`Offre ${user?.plan_name}${user?.free_access ? " (offerte)" : ""}`} text={`Transcription utilisée ce mois-ci : ${usageText(user)}${user?.premium_until && new Date(user.premium_until) > new Date() ? ` · Premium offert (parrainage) ${user?.plan === "free" ? "jusqu'au" : "en réserve jusqu'au"} ${fmtDate(user.premium_until)}` : ""}`}>
          <Link to="/app/offres" data-testid="profile-plans-link" className="bg-brand inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold text-white"><Crown className="h-4 w-4" /> Gérer mon offre</Link>
        </Row>
        <Row title={<span className="inline-flex items-center gap-1.5"><Sparkles className="h-4 w-4 text-primary" /> Modèle d'IA</span>} text="Utilisé pour rédiger tes notes, fiches, quiz et réponses.">
          <Choice testid="ai-model-select" value={user?.ai_model} options={AI} onChange={(v) => patch({ ai_model: v }, "Modèle d'IA mis à jour.")} />
        </Row>
        <Row title={<span className="inline-flex items-center gap-1.5"><AudioLines className="h-4 w-4 text-primary" /> Moteur de transcription</span>} text="Utilisé pour transformer l'audio de tes cours en texte.">
          <Choice testid="stt-engine-select" value={user?.stt_engine} options={STT} onChange={(v) => patch({ stt_engine: v }, "Moteur de transcription mis à jour.")} />
        </Row>
        <Row title="Durée de conservation" text="Tes cours sont supprimés automatiquement après cette durée.">
          <Choice testid="retention-select" value={user?.retention_days} options={RETENTION} onChange={(v) => patch({ retention_days: Number(v) }, "Durée de conservation mise à jour.")} />
        </Row>
      </section>

      <h2 className="mt-12 text-xl font-bold">Apparence</h2>
      <p className="mt-2 text-sm text-muted-foreground">Choisis le dégradé de couleurs du site : boutons, logo, accents et animations.</p>
      <GradientPicker value={user?.theme_gradient} onChange={(id) => { setGradient(id); patch({ theme_gradient: id }, "Dégradé appliqué."); }} />

      <h2 className="mt-12 text-xl font-bold">Confidentialité & données</h2>
      <p className="mt-2 flex gap-2 text-sm text-muted-foreground"><ShieldCheck className="h-4 w-4 shrink-0 text-primary" /> L'audio est supprimé dès la transcription. Tes enregistrements ne sont jamais publiés ni utilisés pour entraîner une IA.</p>
      <section className="card-flat mt-5 px-6">
        <Row title="Exporter mes données" text="Télécharge une copie de ton profil et de tous tes cours (RGPD).">
          <button data-testid="export-data-button" onClick={exportData} className="inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold hover:border-primary/40">{exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Exporter</button>
        </Row>
        <Row title="Supprimer tous mes enregistrements" text="Efface définitivement tous tes cours, transcriptions, fiches et quiz.">
          <Danger testid="delete-all-courses-button" label="Tout supprimer" title="Supprimer tous tes cours ?" text="Cette action est irréversible." onConfirm={deleteCourses} />
        </Row>
        <Row title="Supprimer mon compte" text="Supprime ton compte, tes données et résilie ton abonnement.">
          <Danger testid="delete-account-button" label="Supprimer le compte" title="Supprimer ton compte ?" text="Ton compte, tous tes cours et ton abonnement seront supprimés définitivement." onConfirm={deleteAccount} />
        </Row>
      </section>
    </div>
  );
}
